import React from 'react';
import { Input, Label } from 'reactstrap';
import { MODULE_ORDER } from './moduleOrder';

interface PermissionsTableBodyProps {
    permissions: Record<string, any[]>;            // grouped by module name from backend
    selectedPermissions: number[];
    onCheckboxChange: (permId: number) => void;
    onSelectAll: (moduleName: string, isChecked: boolean) => void;
    isModuleAllSelected: (moduleName: string) => boolean;
}

const ACTIONS = ['view', 'add', 'edit', 'delete', 'transfer', 'approve', 'download', 'print', 'invoice'];

function getPermId(permissions: Record<string, any[]>, moduleName: string, action: string): number | null {
    const perms = permissions[moduleName];
    if (!perms) return null;
    const p = perms.find((x: any) => x.action === action);
    return p ? p.id : null;
}

function ModuleRow({
    moduleName,
    permissions,
    selectedPermissions,
    onCheckboxChange,
    onSelectAll,
    isModuleAllSelected,
}: {
    moduleName: string;
    permissions: Record<string, any[]>;
    selectedPermissions: number[];
    onCheckboxChange: (permId: number) => void;
    onSelectAll: (moduleName: string, isChecked: boolean) => void;
    isModuleAllSelected: (moduleName: string) => boolean;
}) {
    return (
        <tr key={moduleName}>
            <td className="fw-bold bg-light">
                <div className="d-flex align-items-center gap-2">
                    <Input
                        type="checkbox"
                        className="border-primary"
                        id={`select-all-${moduleName}`}
                        checked={isModuleAllSelected(moduleName)}
                        onChange={(e) => onSelectAll(moduleName, e.target.checked)}
                    />
                    <Label htmlFor={`select-all-${moduleName}`} className="mb-0 cursor-pointer text-capitalize text-dark">
                        {moduleName}
                    </Label>
                </div>
            </td>
            {ACTIONS.map(action => {
                const permId = getPermId(permissions, moduleName, action);
                return (
                    <td key={action} className="text-center">
                        {permId ? (
                            <Input
                                type="checkbox"
                                className="border-primary"
                                checked={selectedPermissions.includes(permId)}
                                onChange={() => onCheckboxChange(permId)}
                            />
                        ) : (
                            <span className="text-muted opacity-25">—</span>
                        )}
                    </td>
                );
            })}
        </tr>
    );
}

function SectionHeader({ title }: { title: string }) {
    return (
        <tr style={{ backgroundColor: '#0766AD' }}>
            <td colSpan={10} className="fw-bold py-1 px-3" style={{ fontSize: '14px', letterSpacing: '1px', color: "#0766AD" }}>
                {title.toUpperCase()}
            </td>
        </tr>
    );
}

export default function PermissionsTableBody(props: PermissionsTableBodyProps) {
    const { permissions } = props;

    // Collect all module names that exist in the backend
    const allBackendModules = new Set(Object.keys(permissions));

    // Track which ones we've already rendered via MODULE_ORDER
    const renderedModules = new Set<string>();

    const rows: React.ReactNode[] = [];

    // 1. Render in sidebar order
    MODULE_ORDER.forEach(({ section, modules }) => {
        const visibleModules = modules.filter(m => allBackendModules.has(m));
        if (visibleModules.length === 0) return; // Skip section if none in DB

        rows.push(<SectionHeader key={`section-${section}`} title={section} />);

        visibleModules.forEach(moduleName => {
            renderedModules.add(moduleName);
            rows.push(
                <ModuleRow
                    key={moduleName}
                    moduleName={moduleName}
                    {...props}
                />
            );
        });
    });

    // 2. Append any backend modules NOT in MODULE_ORDER in an "Other" section
    const unknownModules = Array.from(allBackendModules).filter(m => !renderedModules.has(m));
    if (unknownModules.length > 0) {
        rows.push(<SectionHeader key="section-Other" title="Other" />);
        unknownModules.forEach(moduleName => {
            rows.push(
                <ModuleRow
                    key={moduleName}
                    moduleName={moduleName}
                    {...props}
                />
            );
        });
    }

    return <>{rows}</>;
}
